/**
 * TurnstileGate — invisible Cloudflare Turnstile wrapper that becomes a
 * NO-OP when the admin hasn't configured a site_key yet. Renders the
 * widget right above the submit button; the caller passes:
 *
 *   • `action`   — the Turnstile action label (login | register).
 *   • `onToken`  — called with the verified token on success.
 *   • `onExpire` — called when the token times out (5 min).
 *
 * If captcha is disabled in admin config, the component returns null
 * and immediately invokes `onToken("")` once so the form can submit
 * without waiting.
 *
 * iter-169zo — error/expire recovery. Previously a network blip or a
 * Turnstile-side reject would leave the widget in a permanent error
 * state and the parent form stuck on the failure message. Now we:
 *   1. Auto-reset the widget on `onError` / `onExpire` so the user
 *      sees the challenge again without reloading the page.
 *   2. Surface a small inline retry pill so the user knows what
 *      happened and can click to retry manually if the auto-reset
 *      hasn't kicked in yet.
 * Auto-reset is best-effort (the ref may be null right after mount);
 * the manual retry button is the safety net.
 */
import { useEffect, useRef, useState } from "react";
import { Turnstile } from "@marsidev/react-turnstile";
import { useSecurityConfig } from "../lib/useSecurityConfig";
import { tx } from "../i18n";

export function TurnstileGate({ action, onToken, onExpire, lang = "es" }) {
  const { data } = useSecurityConfig();
  const noopFired = useRef(false);
  const widgetRef = useRef(null);
  const [errored, setErrored] = useState(false);
  const enabled = !!data?.turnstile?.enabled;
  const siteKey = data?.turnstile?.site_key || "";

  useEffect(() => {
    if (!enabled && !noopFired.current) {
      noopFired.current = true;
      onToken?.("");
    }
  }, [enabled, onToken]);

  if (!enabled) return null;

  const safeReset = () => {
    setErrored(false);
    try {
      widgetRef.current?.reset?.();
    } catch (_e) {
      /* widget already torn down — nothing to do */
    }
  };

  const t = (es, en, pt) => tx(lang, en, es, pt || es);

  return (
    <div className="flex flex-col items-center gap-1 my-2" data-testid="turnstile-widget-wrap">
      <Turnstile
        ref={widgetRef}
        siteKey={siteKey}
        options={{ size: "flexible", appearance: "interaction-only", action }}
        onSuccess={(token) => {
          setErrored(false);
          onToken?.(token);
        }}
        onExpire={() => {
          onToken?.("");
          onExpire?.();
          // Refresh the widget so the user can immediately retry.
          safeReset();
        }}
        onError={() => {
          onToken?.("");
          setErrored(true);
          // Best-effort auto-recovery so a single Turnstile blip doesn't
          // brick the form. The retry button below is the manual escape
          // hatch when this fails.
          setTimeout(safeReset, 50);
        }}
      />
      {errored && (
        <button /* design-system: ALLOW legacy-todo */
          type="button"
          onClick={safeReset}
          data-testid="turnstile-retry-btn"
          className="text-[11px] text-amber-700 hover:text-amber-900 underline underline-offset-2"
        >
          {t(
            "La verificación falló. Toca aquí para reintentar.",
            "Verification failed. Tap here to retry.",
          )}
        </button>
      )}
    </div>
  );
}
